/**
 * Preview wag — the smallest T2b (ADR-031 §9.3 "does it move like a tail?"):
 * a procedural, SESSION-ONLY sine wag over a grafted tail chain, so a person
 * can see the chain bend down the middle of the flesh without authoring a
 * clip.
 *
 * WHAT IT IS NOT. It is not motion the character owns: nothing here touches
 * the Document, the command log or any export. It rides the viewport's
 * render-preview slot — applied immediately before a frame renders and
 * RESTORED immediately after — so between frames every tail bone holds
 * exactly the rotation it had (the rest, a pose, or nothing a clip drives),
 * and any door that reads the scene outside the render (the export, the
 * package, the bake) reads the character without the wag.
 *
 * THE MOTION. Each tail bone rests in the pelvis's frame (graftTail), so one
 * rotation about local +Y (the body's up) wags every bone the same way:
 * `amp · sin(2π·f·t − k·lag)`, bone k root → tip, so the wave travels down
 * the tail — the base leads, the tip follows (the actuation-at-the-base
 * prior). Layered OVER whatever the bone holds this frame (post-multiplied),
 * so it plays over a walk or on a character held still.
 *
 * Depends on: three. Depended on by: main (plays whenever a chain exists, outside pose mode).
 */
import { Quaternion, Vector3, type Object3D } from 'three';

export interface TailWagOptions {
  /** Degrees per bone at the crest (default 10). */
  readonly amplitudeDeg?: number;
  /** Cycles per second (default 0.8). */
  readonly frequencyHz?: number;
  /** Phase lag per bone, radians (default 0.35) — the travelling wave. */
  readonly lagRad?: number;
}

export interface TailWagProbe {
  /** Frames the wag was applied on since it was created. */
  readonly frames: number;
  /** The root bone's angle on the last applied frame, degrees. */
  readonly lastRootDeg: number;
  readonly bones: number;
}

export interface TailWag {
  /**
   * Apply this frame's wag to `bones` (root → tip) at time `seconds`; returns
   * the restore, which puts every touched quaternion back bit-for-bit.
   * `offsets` (ADR-038 R5): the tail drive's per-bone local offsets — the
   * mood played without physics, in place of the built-in sine.
   */
  apply(bones: readonly Object3D[], seconds: number, offsets?: readonly Quaternion[]): () => void;
  probe(): TailWagProbe;
}

const UP = new Vector3(0, 1, 0);

export function createTailWag(options: TailWagOptions = {}): TailWag {
  const amp = ((options.amplitudeDeg ?? 10) * Math.PI) / 180;
  const freq = options.frequencyHz ?? 0.8;
  const lag = options.lagRad ?? 0.35;
  const q = new Quaternion();
  let frames = 0;
  let lastRootDeg = 0;
  let lastBones = 0;
  return {
    apply(bones, seconds, offsets) {
      const saved = bones.map((b) => b.quaternion.clone());
      bones.forEach((b, k) => {
        const off = offsets?.[k];
        if (off !== undefined) {
          if (k === 0) lastRootDeg = (2 * Math.acos(Math.min(1, Math.abs(off.w))) * 180) / Math.PI;
          b.quaternion.multiply(off);
          return;
        }
        const angle = amp * Math.sin(2 * Math.PI * freq * seconds - k * lag);
        if (k === 0) lastRootDeg = (angle * 180) / Math.PI;
        b.quaternion.multiply(q.setFromAxisAngle(UP, angle));
      });
      frames += 1;
      lastBones = bones.length;
      return () => {
        bones.forEach((b, k) => b.quaternion.copy(saved[k]!));
        // The renderer baked the wag into matrixWorld; put the WORLD back too,
        // or any reader of matrices between frames (the skeleton handles, a
        // pick, the export) sees the wagged chain (measured: the pose tool's
        // handle and its bead disagreed and the tail could not be picked).
        bones[0]?.updateMatrixWorld(true);
      };
    },
    probe: () => ({ frames, lastRootDeg, bones: lastBones }),
  };
}
